import { useCallback } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { CoinState, TabletopItemType } from '@/components/tabletop/types';
import TabletopItem from '@/components/tabletop/components/TabletopItem';

interface CoinProps {
  coin: CoinState;
  onDragStart?: (id: string, type: TabletopItemType, event: React.MouseEvent) => void;
  onSelect?: (id: string, type: TabletopItemType, event: ReactMouseEvent) => void;
  onFlip?: (id: string) => void;
  isSelected?: boolean;
  className?: string;
  viewerRotation?: number;
}

const DEFAULT_COIN_SIZE = 58;

export default function Coin({
  coin,
  onDragStart,
  onSelect,
  onFlip,
  isSelected = false,
  className = '',
  viewerRotation = 0
}: CoinProps) {
  const { id, position, rotation, side, size, zIndex, flipId } = coin;
  const labelRotation = -(rotation + viewerRotation);
  const displaySize = size || DEFAULT_COIN_SIZE;
  const isHeads = side === 'heads';

  const handleItemSelect = useCallback((id: string, type: TabletopItemType, event: ReactMouseEvent) => {
    onSelect?.(id, type, event);
  }, [onSelect]);

  const handleFlip = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onFlip?.(id);
  }, [id, onFlip]);

  return (
    <TabletopItem
      id={id}
      type="coin"
      position={position}
      rotation={rotation}
      width={displaySize}
      height={displaySize}
      isSelected={isSelected}
      onSelect={handleItemSelect}
      onDragStart={onDragStart}
      className={className}
      zIndex={zIndex}
    >
      <div
        key={flipId ?? 0}
        className={`coin-face relative flex h-full w-full items-center justify-center rounded-full border-2 border-amber-700/70 bg-amber-300 shadow-[0_14px_28px_rgba(15,23,42,0.22)] ${(flipId ?? 0) > 0 ? 'coin-face-flipping' : ''}`}
        onContextMenu={handleFlip}
        onDoubleClick={handleFlip}
      >
        <div className="pointer-events-none absolute inset-[4px] rounded-full border border-amber-100/70" />
        <div className="pointer-events-none absolute inset-[9px] rounded-full border border-amber-700/30" />
        <div className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-b from-white/45 via-transparent to-amber-900/18" />
        <span
          className="select-none font-black text-amber-950"
          style={{
            fontSize: `${Math.min(displaySize * 0.42, 25)}px`,
            textShadow: '0 1px 0 rgba(255, 255, 255, 0.55)',
            transform: `rotate(${labelRotation}deg)`,
            transformOrigin: 'center'
          }}
        >
          {isHeads ? 'H' : 'T'}
        </span>
      </div>
    </TabletopItem>
  );
}
